import React, { FC } from 'react';

import Dropzone from '@adsk/alloy-react-dropzone';
import { WithAlloySuspense } from '@adsk/alloy-react-suspense';

import AttachmentsDropzone, {
  AttachmentsDropzoneProps,
} from './AttachmentsDropzone';
import { UPLOAD_METHODS } from '../../consts';
import { useAttachmentsContext } from '../../context/AttachmentsContext';
import useCapacityAlert from '../../hooks/useCapacityAlert';

const ConnectedAttachmentsDropzone: FC<AttachmentsDropzoneProps> = ({
  size,
  ...props
}) => {
  const {
    attachments,
    drafts,
    totalAttachments,
    maxNumberOfAttachments,
    addAttachments,
    handleRejectedAttachments,
    openProjectFilesModal,
    allowDelete,
    readOnly,
    enableUploadOnPaste,
  } = useAttachmentsContext();

  const isEmpty = !attachments.length && !drafts.length;

  const totalAttachmentsCount =
    (totalAttachments ?? attachments.length) + drafts.length;
  const isExceedsMaxNumberOfAttachments =
    !!maxNumberOfAttachments && totalAttachmentsCount >= maxNumberOfAttachments;

  const maxNumberOfAttachmentsAllowed =
    maxNumberOfAttachments &&
    Math.max(0, maxNumberOfAttachments - totalAttachmentsCount);

  const { notifyCapacityLimitIfNeeded } = useCapacityAlert({
    maxNumberOfAttachments,
  });

  return (
    <AttachmentsDropzone
      size={isEmpty ? size : Dropzone.SIZES.SMALL}
      maxFiles={maxNumberOfAttachmentsAllowed}
      disabled={Boolean(isExceedsMaxNumberOfAttachments)}
      isExceedsMaxFiles={isExceedsMaxNumberOfAttachments}
      maxNumberOfAttachments={maxNumberOfAttachments}
      openProjectFilesModal={openProjectFilesModal}
      allowDelete={allowDelete}
      readOnly={readOnly}
      enableUploadOnPaste={enableUploadOnPaste}
      onDropAccepted={(files, event) => {
        notifyCapacityLimitIfNeeded({ totalAttachmentsCount });
        addAttachments({
          files,
          method:
            event.type === 'drag' ? UPLOAD_METHODS.DRAG : UPLOAD_METHODS.BROWSE,
        });
      }}
      onDropRejected={(rejectedFiles) => {
        handleRejectedAttachments({ files: rejectedFiles });
      }}
      {...props}
    />
  );
};

const SuspendedConnectedAttachmentsDropzone: FC<AttachmentsDropzoneProps> =
  WithAlloySuspense(ConnectedAttachmentsDropzone);
export default SuspendedConnectedAttachmentsDropzone;
